import React, { useState } from "react";
import { Box, Tooltip, Menu } from "@mui/material";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useTranslation from "../../../shared/hooks/useTranslation";
import { CompanyGroup } from "../../companyGroups/models/models";

const dt = {
  cardBg: "#FFFFFF", cardBorder: "#E5E7EB", textPrimary: "#1a1a2e",
  textSecondary: "#6B7280", textMuted: "#9CA3AF", blue: "var(--accent, #3498DB)",
  amber: "#F59E0B", amberSoft: "rgba(245, 158, 11, 0.08)",
  radius: "14px", radiusSm: "8px", font: 'var(--font-sans)',
};

interface SaveDropdownProps {
  companyId: string;
  groups: CompanyGroup[];
  isSaved: boolean;
  onToggleGroup: (groupId: string, isInGroup: boolean) => void;
  onCreateGroup: (name: string) => void;
  /** Render as a compact 32x32 icon-only bookmark button */
  compact?: boolean;
}

const SaveDropdown = ({ companyId, groups, isSaved, onToggleGroup, onCreateGroup, compact = false }: SaveDropdownProps) => {
  const { translate } = useTranslation("partnerHub");
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewInput, setShowNewInput] = useState(false);
  const menuOpen = Boolean(menuAnchor);

  const handleMenuClose = () => { setMenuAnchor(null); setShowNewInput(false); setNewGroupName(""); };

  const isCompanyInGroup = (group: CompanyGroup): boolean => {
    const cId = companyId?.toLowerCase();
    return group.items?.some((item) => item.customer?.id?.toLowerCase() === cId) ?? false;
  };
  const handleToggle = (group: CompanyGroup) => { onToggleGroup(group.id, isCompanyInGroup(group)); };
  const handleCreateGroup = () => {
    if (newGroupName.trim()) { onCreateGroup(newGroupName.trim()); setNewGroupName(""); setShowNewInput(false); }
  };

  return (
    <Box>
      {compact ? (
        <Tooltip title={isSaved ? translate("save.saved") : translate("save.save")} enterDelay={200} arrow>
          <Box
            onClick={(e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)}
            sx={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: dt.radiusSm,
              border: `1px solid ${isSaved ? dt.amber + "40" : dt.cardBorder}`,
              background: isSaved ? dt.amberSoft : dt.cardBg,
              color: isSaved ? dt.amber : dt.textSecondary,
              cursor: "pointer",
              transition: "all .2s",
              "&:hover": {
                borderColor: isSaved ? dt.amber : dt.blue,
                color: isSaved ? dt.amber : dt.blue,
                boxShadow: `0 0 0 3px ${isSaved ? "rgba(245,158,11,0.08)" : "var(--accent-soft, rgba(52,152,219,0.06))"}`,
              },
            }}
          >
            {isSaved ? <BookmarkIcon sx={{ fontSize: 17 }} /> : <BookmarkBorderOutlinedIcon sx={{ fontSize: 17 }} />}
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box onClick={(e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)} sx={{
            display: "flex", alignItems: "center", gap: "6px", padding: "9px 16px", borderRadius: dt.radiusSm,
            background: isSaved ? dt.amberSoft : dt.cardBg, border: `1px solid ${isSaved ? dt.amber + "40" : dt.cardBorder}`,
            color: isSaved ? dt.amber : dt.textSecondary, fontSize: "13px", fontWeight: 500, cursor: "pointer",
            fontFamily: dt.font, transition: "all .2s",
            "&:hover": { borderColor: isSaved ? dt.amber : dt.blue, color: isSaved ? dt.amber : dt.blue },
          }}>
            {isSaved ? <BookmarkIcon sx={{ fontSize: 15 }} /> : <BookmarkBorderOutlinedIcon sx={{ fontSize: 15 }} />}
            {isSaved ? translate("save.saved") : translate("save.save")}
            <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 13 }} />
          </Box>
          <Tooltip title={translate("save.tooltip")} enterDelay={300} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: dt.textMuted, cursor: "help" }} />
          </Tooltip>
        </Box>
      )}

      <Menu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 260, maxHeight: 320, borderRadius: "14px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", border: `1px solid ${dt.cardBorder}`, mt: 0.5, p: "8px" } } }}>
        <Box sx={{ padding: "6px 10px", fontSize: "11px", color: dt.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: dt.font }}>
          {translate("groups.saveToGroup")}
        </Box>
        <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
          {groups.map((group) => {
            const isIn = isCompanyInGroup(group);
            return (
              <Box key={group.id} sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", padding: "7px 10px", borderRadius: dt.radiusSm, color: dt.textPrimary, fontSize: "13px", fontFamily: dt.font, "&:hover": { background: "#F4F6F9" } }}>
                <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.name}</Box>
                <Box sx={{ fontSize: "11px", color: dt.textMuted, mr: "4px" }}>{group.items?.length ?? 0}</Box>
                {isIn ? (
                  <Box onClick={() => handleToggle(group)} sx={{ fontSize: "11px", fontWeight: 600, padding: "2px 10px", borderRadius: "4px", cursor: "pointer", fontFamily: dt.font, color: "#EF4444", border: "1px solid #FECACA", background: "#FEF2F2", whiteSpace: "nowrap", "&:hover": { background: "#FEE2E2", borderColor: "#EF4444" } }}>
                    {translate("groups.remove")}
                  </Box>
                ) : (
                  <Box onClick={() => handleToggle(group)} sx={{ fontSize: "11px", fontWeight: 600, padding: "2px 10px", borderRadius: "4px", cursor: "pointer", fontFamily: dt.font, color: dt.blue, border: `1px solid ${dt.blue}40`, background: `${dt.blue}08`, whiteSpace: "nowrap", "&:hover": { background: `${dt.blue}18`, borderColor: dt.blue } }}>
                    {translate("groups.add")}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ height: "1px", background: dt.cardBorder, my: "6px" }} />
        {!showNewInput ? (
          <Box onClick={() => setShowNewInput(true)} sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", padding: "8px 10px", borderRadius: dt.radiusSm, color: dt.blue, fontSize: "13px", cursor: "pointer", fontFamily: dt.font, "&:hover": { background: "#F4F6F9" } }}>
            <AddOutlinedIcon sx={{ fontSize: 14 }} />{translate("groups.newGroup")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: "6px", padding: "4px 10px" }}>
            <input autoFocus value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleCreateGroup()} placeholder={translate("groups.newGroup")}
              style={{ flex: 1, border: `1px solid ${dt.cardBorder}`, borderRadius: "6px", padding: "6px 10px", fontSize: "13px", fontFamily: 'var(--font-sans)', color: dt.textPrimary, outline: "none", background: "#F8FAFC" }} />
            <Box onClick={handleCreateGroup} sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "6px", background: dt.blue, color: "#fff", cursor: "pointer", flexShrink: 0, fontSize: "16px", fontWeight: 700 }}>+</Box>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default SaveDropdown;
